// services/storyIdeasApi.ts — HTTP client for POST /api/story-ideas.
//
// Player-side surface for the "story idea / feedback" submission flow.
// Mirrors storiesApi.ts error-class hierarchy so callers can do a single
// `instanceof StoryIdeasApiError` check and narrow to the specific
// subclass for the contractual error codes:
//   - 400 invalid_body → InvalidIdeaError
//   - 429 rate_limited → IdeasRateLimitedError (carries retryAfterSeconds)
//   - everything else → plain StoryIdeasApiError
//
// See STORIES_CONTRACT.md §9.

import { authedFetch } from "../auth/tokens";

// ── Response shapes ─────────────────────────────────────────────────────

export interface SubmitIdeaResponse {
  id: string;
  /** ISO timestamp from the server. */
  createdAt: string;
}

// ── Errors ──────────────────────────────────────────────────────────────

export class StoryIdeasApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly body?: unknown;
  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = "StoryIdeasApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** 400 — body failed validation (length or shape). */
export class InvalidIdeaError extends StoryIdeasApiError {
  constructor(message: string, body?: unknown) {
    super(message, 400, "invalid_body", body);
    this.name = "InvalidIdeaError";
  }
}

/**
 * 429 — caller has exceeded the per-user 24h submission cap (5/day).
 * `retryAfterSeconds` comes from the server's body (and matches the
 * Retry-After header).
 */
export class IdeasRateLimitedError extends StoryIdeasApiError {
  readonly retryAfterSeconds: number;
  constructor(retryAfterSeconds: number, body?: unknown) {
    super("rate_limited", 429, "rate_limited", body);
    this.name = "IdeasRateLimitedError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

// ── Helper: parse response, throw on non-2xx ────────────────────────────

interface ServerErrBody {
  error?: string;
  code?: string;
  retryAfterSeconds?: number;
}

async function parseResponse<T>(res: Response): Promise<T> {
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Non-JSON — fall through.
    }
  }
  if (!res.ok) {
    const errBody = (body as ServerErrBody | null) ?? {};
    const code = errBody.code;
    const message =
      errBody.error ||
      (text ? text.slice(0, 200) : `Request failed with status ${res.status}`);

    if (res.status === 400 && code === "invalid_body") {
      throw new InvalidIdeaError(message, body);
    }
    if (res.status === 429 && code === "rate_limited") {
      const retryAfterSeconds =
        typeof errBody.retryAfterSeconds === "number" &&
        Number.isFinite(errBody.retryAfterSeconds)
          ? errBody.retryAfterSeconds
          : 24 * 60 * 60;
      throw new IdeasRateLimitedError(retryAfterSeconds, body);
    }
    throw new StoryIdeasApiError(message, res.status, code, body);
  }
  return body as T;
}

// ── Endpoint ────────────────────────────────────────────────────────────

/**
 * POST /api/story-ideas — submit a free-form idea / feedback ticket.
 *
 * Throws:
 *   - InvalidIdeaError on 400 (10..4000 char rule)
 *   - IdeasRateLimitedError on 429 (5/24h per user)
 *   - StoryIdeasApiError on any other non-2xx (incl. 401, 500)
 */
export async function submitStoryIdea(
  body: string,
): Promise<SubmitIdeaResponse> {
  const res = await authedFetch(`/api/story-ideas`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
  return parseResponse<SubmitIdeaResponse>(res);
}
